import { Box, Theme, Tooltip, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  BaseEdge,
  Edge,
  EdgeProps,
  Handle,
  Node,
  NodeProps,
  Position,
} from "@xyflow/react";
import React from "react";

import { ChartPalette, labelBacking, monospaceStyle } from "../../../theme";
import {
  MissionCapability,
  MissionCategory,
  MissionCentre,
  MissionEdge,
  MissionLevel,
  MissionProvider,
} from "../../../types/analytics";
import { counted } from "../Ecosystem/EcosystemParts";
import { hatchOf } from "../Treemap/TreemapCanvas";
import { LABEL_WIDTH, MissionRing, ringOfCategory } from "./missionsLayout";

// The lines, as the design draws them: a spoke from the centre to a
// capability, a provider on record inside the hosts solid, one that could
// provide dashed, and a stated "enables" in teal. The selection takes the
// accent.
export const COULD_DASH = "5 5";
// The strokes of the lines, from the chart palette: the spoke's `edge`, the
// inside provider's `edgeFaint`, the could provider's `edgeSoft` and the
// stated "enables" in `enables`.
export const lineColoursOf = (chart: ChartPalette) =>
  ({
    spoke: chart.edge,
    inside: chart.edgeFaint,
    could: chart.edgeSoft,
    enables: chart.enables,
  }) as const;
// The text of an accent figure, the lift or the centre's host count, is
// `chart.accentText`.
// Two colours beside the accent, one for each half of the rings, so that
// colour stays sparing: the capabilities violet, the categories pink (the
// ecosystem's colour of a kind). The whole level of each half (a platform
// behaviour, an end item) is filled; what is fitted into it is not. The
// missions, miniatures of the centre, take the accent. They are
// `chart.capability` and `chart.category`, with their text.
// The colour of each ring's guide: the missions' in the accent
export const ringColour = (ring: MissionRing, theme: Theme): string =>
  ring === 1
    ? theme.palette.accent.main
    : ring <= 3
      ? theme.palette.chart.capability
      : theme.palette.chart.category;
// How strongly a ring's guide is drawn: its width, and the opacity of its
// colour, the whole level a little stronger than what is fitted into it
const RING_WIDTH = 1.5;
const RING_OPACITY: Record<MissionRing, number> = { 1: 0.42, 2: 0.42, 3: 0.3, 4: 0.42, 5: 0.3 };
// The opacity of a node or a line the selection leaves out
const NODE_DIMMED = 0.42;
const LINE_DIMMED = 0.25;
const ENABLES_OPACITY = 0.8;

// The marks of the nodes: the centre's circle, a mission's small circle, a
// capability's pill and a category's box. A node sits on its ring by the
// centre of its mark.
export const MISSION_SIZE = 96;
export const MISSION_DOT_SIZE = 22;
export const PILL_HEIGHT = 24;
export const BOX_SIZE = { width: 46, height: 34 } as const;

// The height of a capability's mark, by its level
export const markHeight = (level: MissionLevel): number =>
  level === "operator" ? MISSION_DOT_SIZE : PILL_HEIGHT;

// The letter a capability's pill carries: its level. A mission's circle
// carries none.
export const LEVEL_LETTERS: Record<MissionLevel, string> = {
  operator: "",
  platform: "P",
  infrastructure: "I",
  module: "M",
  component: "C",
  unknown: "?",
};

// A platform-level capability is a behaviour of the whole drone: its pill
// is drawn brighter
const isBehaviour = (level: MissionLevel): boolean =>
  level === "platform" || level === "infrastructure";

// The lift as the design writes it: ×1.75
export const liftText = (lift: number): string =>
  `×${Number(lift.toFixed(2)).toString()}`;

export const percent = (share: number): string => `${Math.round(share * 100)}%`;

// Words an abbreviation skips
const SMALL_WORDS = new Set(["a", "an", "and", "of", "the", "for", "to", "&"]);

// A short name for a box or a tight line: an acronym the name opens with,
// else the initials of its words, else its first two letters
export const shortName = (name: string): string => {
  const words = name.split(/[\s,]+/).filter((word) => word && !SMALL_WORDS.has(word.toLowerCase()));
  const first = words[0] ?? "";
  if (first.length > 1 && first.length <= 5 && first === first.toUpperCase() && /[A-Z]/.test(first)) {
    return first;
  }
  if (words.length > 1) {
    return words
      .slice(0, 4)
      .map((word) => word[0].toUpperCase())
      .join("");
  }
  return first.slice(0, 2).toUpperCase();
};

export const capabilityName = (capability: MissionCapability): string =>
  capability.capability.name ?? capability.capability.id;

export const categoryName = (category: { category: { id: string; name: string | null } }): string =>
  category.category.name ?? category.category.id;

// What a category says beneath its name: its hosts inside and makers, or
// the products that could provide
export const categoryCaption = (category: MissionCategory): string =>
  category.inside
    ? `${category.inside_hosts} inside · ${counted(category.makers, "maker")}`
    : `could · ${counted(category.products, "product")}`;

// A short stretch of line, as the canvas draws it
export const LineSample: React.FC<{ stroke: string; dash?: string }> = ({ stroke, dash }) => (
  <Box component="svg" width={26} height={8} aria-hidden sx={{ flexShrink: 0, verticalAlign: "middle" }}>
    <line x1={0} y1={4} x2={26} y2={4} stroke={stroke} strokeWidth={2} strokeDasharray={dash} />
  </Box>
);

// A colour laid over the paper as a tint, so that no line shows through
const tintOf = (colour: string, opacity: number): string =>
  `linear-gradient(${alpha(colour, opacity)}, ${alpha(colour, opacity)})`;

// The look of a level's mark, on the canvas and in the panel: a platform
// behaviour filled in the capabilities' violet, a module or component
// capability outlined in it
const levelLook = (level: MissionLevel) => {
  const whole = isBehaviour(level);
  return {
    border: (theme: Theme) =>
      `1.5px solid ${alpha(theme.palette.chart.capability, whole ? 0.85 : 0.5)}`,
    backgroundColor: "background.paper",
    backgroundImage: (theme: Theme) =>
      tintOf(theme.palette.chart.capability, whole ? 0.26 : 0.08),
    color: whole ? (theme: Theme) => theme.palette.chart.capabilityText : "text.secondary",
  };
};

// A level's letter in a small round mark, as the legend and the panel
// carry it
export const LevelMark: React.FC<{ level: MissionLevel; small?: boolean }> = ({
  level,
  small = false,
}) =>
  level === "operator" ? (
    <MissionDot size={small ? 14 : 18} />
  ) : (
    <Box
      component="span"
      aria-hidden
      sx={{
        ...monospaceStyle,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        minWidth: small ? 14 : 18,
        height: small ? 14 : 18,
        px: "3px",
        borderRadius: "9px",
        fontSize: small ? 9 : 10,
        ...levelLook(level),
        verticalAlign: "middle",
      }}
    >
      {LEVEL_LETTERS[level]}
    </Box>
  );

// A mission's small circle on its ring: a miniature of the centre's mark,
// its border in the accent and the accent's tint over the paper, no letter
export const MissionDot: React.FC<{ size?: number; selected?: boolean }> = ({
  size = MISSION_DOT_SIZE,
  selected = false,
}) => {
  const theme = useTheme();
  const accent = theme.palette.accent.main;
  return (
    <Box
      component="span"
      aria-hidden
      data-testid="mission-dot"
      sx={{
        flexShrink: 0,
        display: "inline-block",
        boxSizing: "border-box",
        width: size,
        height: size,
        borderRadius: "50%",
        border: `${size >= MISSION_DOT_SIZE ? 2 : 1.5}px solid ${accent}`,
        backgroundColor: "background.paper",
        backgroundImage: tintOf(accent, 0.22),
        verticalAlign: "middle",
        ...(selected ? { boxShadow: `0 0 0 4px ${alpha(accent, 0.3)}` } : {}),
      }}
    />
  );
};

// A category's box in the categories' pink: solid on record inside, dashed
// when it could only provide, hatched under the floor. A box on record is
// tinted, an end item's (a platform, infrastructure or equipment) more
// strongly. Half size and empty as a legend sample.
export const CategoryBox: React.FC<{
  label: string;
  inside: boolean;
  thin: boolean;
  endItem?: boolean;
  selected?: boolean;
  sample?: boolean;
}> = ({ label, inside, thin, endItem = false, selected = false, sample = false }) => {
  const theme = useTheme();
  const { chart } = theme.palette;
  const accent = theme.palette.accent.main;
  return (
    <Box
      component="span"
      aria-hidden
      data-testid={sample ? "missions-sample" : "category-box"}
      sx={{
        ...monospaceStyle,
        flexShrink: 0,
        boxSizing: "border-box",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: sample ? 20 : BOX_SIZE.width,
        height: sample ? 14 : BOX_SIZE.height,
        borderRadius: sample ? "4px" : "6px",
        fontSize: 10,
        overflow: "hidden",
        borderWidth: 1.5,
        borderStyle: inside ? "solid" : "dashed",
        borderColor: alpha(chart.category, thin ? 0.22 : inside ? 0.75 : 0.5),
        // A dashed box reads as empty on the canvas's own paper, which also
        // keeps the lines from showing through it
        backgroundColor: "background.paper",
        backgroundImage: thin
          ? hatchOf(theme)
          : inside
            ? tintOf(chart.category, endItem ? 0.26 : 0.12)
            : undefined,
        color: thin ? "text.disabled" : inside ? chart.categoryText : "text.secondary",
        ...(selected
          ? {
              borderColor: accent,
              color: "text.primary",
              boxShadow: `0 0 0 4px ${alpha(accent, 0.3)}`,
            }
          : {}),
      }}
    >
      {!sample && label}
    </Box>
  );
};

// A capability's pill: the letter of its level, filled for a platform
// behaviour
export const CapabilityPill: React.FC<{
  level: MissionLevel;
  selected?: boolean;
  sample?: boolean;
}> = ({ level, selected = false, sample = false }) => {
  const theme = useTheme();
  const accent = theme.palette.accent.main;
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        ...monospaceStyle,
        boxSizing: "border-box",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: sample ? 18 : 30,
        height: sample ? 14 : PILL_HEIGHT,
        px: sample ? "3px" : "7px",
        borderRadius: `${(sample ? 14 : PILL_HEIGHT) / 2}px`,
        fontSize: sample ? 9 : "10.5px",
        ...levelLook(level),
        ...(selected
          ? {
              borderColor: accent,
              color: "text.primary",
              boxShadow: `0 0 0 4px ${alpha(accent, 0.3)}`,
            }
          : {}),
      }}
    >
      {LEVEL_LETTERS[level]}
    </Box>
  );
};

// The centre's circle, filled in the accent; a sample for the legend
export const MissionMark: React.FC<{ sample?: boolean; children?: React.ReactNode }> = ({
  sample = false,
  children,
}) => {
  const theme = useTheme();
  const accent = theme.palette.accent.main;
  return (
    <Box
      component="span"
      aria-hidden={sample || undefined}
      sx={{
        flexShrink: 0,
        boxSizing: "border-box",
        width: sample ? 16 : MISSION_SIZE,
        height: sample ? 16 : MISSION_SIZE,
        borderRadius: "50%",
        border: `2px solid ${accent}`,
        // The tint is laid over the paper, so that no spoke shows through
        backgroundColor: "background.paper",
        backgroundImage: `linear-gradient(${alpha(accent, sample ? 0.3 : 0.22)}, ${alpha(accent, sample ? 0.3 : 0.22)})`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "2px",
        color: "text.primary",
        boxShadow: sample ? undefined : `0 0 0 4px ${alpha(accent, 0.3)}`,
      }}
    >
      {children}
    </Box>
  );
};

export interface MissionRingData extends Record<string, unknown> {
  ring: MissionRing;
}

export type MissionRingFlowNode = Node<MissionRingData, "ring">;

// A ring's guide, drawn beneath the nodes: a solid ellipse in the colour of
// its half, the missions' in the accent (a dashed line here says "could
// provide"), the whole level stronger than what is fitted into it
export const MissionRingNode: React.FC<NodeProps<MissionRingFlowNode>> = ({ data }) => {
  const theme = useTheme();
  const colour = alpha(ringColour(data.ring, theme), RING_OPACITY[data.ring]);
  return (
    <Box
      role="group"
      aria-label={`Ring ${data.ring}`}
      data-testid="ring-guide"
      data-ring={data.ring}
      data-colour={ringColour(data.ring, theme)}
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        border: `${RING_WIDTH}px solid ${colour}`,
        boxSizing: "border-box",
        pointerEvents: "none",
      }}
    />
  );
};

// A short arc of a ring's guide, as the ring key carries it
export const RingSample: React.FC<{ ring: MissionRing }> = ({ ring }) => {
  const theme = useTheme();
  return (
    <Box component="svg" width={18} height={8} aria-hidden sx={{ flexShrink: 0, verticalAlign: "middle" }}>
      <path
        d="M 1 7 Q 9 0 17 7"
        fill="none"
        stroke={alpha(ringColour(ring, theme), Math.min(1, RING_OPACITY[ring] * 2))}
        strokeWidth={RING_WIDTH}
      />
    </Box>
  );
};

// The hidden ends of a node's lines: the lines are drawn from the layout's
// coordinates, but the canvas draws an edge only between two handles
const NodeHandles: React.FC<{ top: number }> = ({ top }) => {
  const style: React.CSSProperties = { left: "50%", top, opacity: 0, pointerEvents: "none" };
  return (
    <>
      <Handle type="target" position={Position.Top} isConnectable={false} style={style} />
      <Handle type="source" position={Position.Bottom} isConnectable={false} style={style} />
    </>
  );
};

export interface CentreNodeData extends Record<string, unknown> {
  centre: MissionCentre;
}

export type CentreFlowNode = Node<CentreNodeData, "centre">;

const refName = (ref: { id: string; name: string | null }): string => ref.name ?? ref.id;

export const centreName = (centre: MissionCentre): string => refName(centre.capability);

// The centre's qualifiers in a short line: "at night · autonomous"; empty
// for none
export const qualifierText = (centre: Pick<MissionCentre, "condition" | "mode">): string =>
  [centre.condition, centre.mode]
    .filter((ref): ref is NonNullable<typeof ref> => ref !== null)
    .map(refName)
    .join(" · ");

// What the centre says where the pointer rests on it: the capability and
// the row above it, its qualifiers, and the kinds of product that carry it.
// Nothing hangs beneath the circle, where it stood in the way of the rings.
export const centreReading = (centre: MissionCentre): string => {
  const name = centreName(centre);
  const qualifiers = qualifierText(centre);
  const kinds = centre.host_kinds.map(
    (kind) => `${kind.category.name ?? kind.category.id} ${kind.hosts}`,
  );
  return [
    centre.parent ? `${name} · ${refName(centre.parent)}` : name,
    ...(qualifiers ? [`stated ${qualifiers}`] : []),
    ...(kinds.length > 0 ? [`carried by ${kinds.join(", ")}`] : []),
  ].join("\n");
};

// The capability at the centre: its name, its qualifiers and its host count
// in the circle
export const CentreNode: React.FC<NodeProps<CentreFlowNode>> = ({ data }) => {
  const { centre } = data;
  const name = centreName(centre);
  const qualifiers = qualifierText(centre);
  return (
    <Box
      role="group"
      aria-label={`Centre: ${name}`}
      title={centreReading(centre)}
      data-testid="mission-centre"
      sx={{
        width: LABEL_WIDTH,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "6px",
        textAlign: "center",
      }}
    >
      <NodeHandles top={MISSION_SIZE / 2} />
      <MissionMark>
        <Box
          component="span"
          sx={{
            px: 1,
            fontSize: 12.5,
            fontWeight: 600,
            lineHeight: 1.2,
            overflowWrap: "anywhere",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {name}
        </Box>
        {qualifiers && (
          <Box
            component="span"
            data-testid="centre-qualifiers"
            sx={{
              px: 1,
              fontSize: "10.5px",
              lineHeight: 1.2,
              color: "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            {qualifiers}
          </Box>
        )}
        <Box component="span" sx={{ ...monospaceStyle, fontSize: "10.5px", color: (theme) => theme.palette.chart.accentText }}>
          {counted(centre.hosts, "host")}
        </Box>
      </MissionMark>
    </Box>
  );
};

interface SelectableData extends Record<string, unknown> {
  selected: boolean;
  dimmed: boolean;
  onSelect: () => void;
}

export interface CapabilityNodeData extends SelectableData {
  capability: MissionCapability;
  // What a capability with no line to a category says of its providers;
  // null for one with a line, and for a mission, which has none to draw
  noProvider: string | null;
}

// What a capability that is no mission and has no provider line drawn
// says, by the kinds of provider asked for
export const noProviderText = (providers: readonly MissionProvider[]): string =>
  providers.length === 1
    ? providers[0] === "inside"
      ? "none on record inside"
      : "none that could provide"
    : "no provider on record";

export type CapabilityFlowNode = Node<CapabilityNodeData, "capability">;

export interface CategoryNodeData extends SelectableData {
  category: MissionCategory;
}

export type CategoryFlowNode = Node<CategoryNodeData, "category">;

// What a node of a ring answers: a click or Enter selects it, and the click
// does not reach the canvas, which would clear the selection
const selectable = (onSelect: () => void) => ({
  role: "button",
  tabIndex: 0,
  className: "nodrag nopan",
  onClick: (event: React.MouseEvent) => {
    event.stopPropagation();
    onSelect();
  },
  onKeyDown: (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect();
    }
  },
});

const NODE_SX = {
  position: "relative",
  width: LABEL_WIDTH,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "3px",
  textAlign: "center",
  cursor: "pointer",
  transition: "opacity 120ms",
  borderRadius: "6px",
  "&:focus-visible": { outline: "2px solid", outlineColor: "link.main", outlineOffset: 3 },
} as const;

// A name on two lines at the most, on its backing
const NameLine: React.FC<{ name: string; quiet?: boolean }> = ({ name, quiet = false }) => (
  <Box
    component="span"
    title={name}
    sx={{
      maxWidth: "100%",
      px: 0.5,
      borderRadius: "4px",
      backgroundColor: labelBacking,
      color: quiet ? "text.secondary" : "text.primary",
      fontSize: "11.5px",
      fontWeight: quiet ? 400 : 500,
      lineHeight: 1.3,
      overflowWrap: "anywhere",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    }}
  >
    {name}
  </Box>
);

const COUNT_SX = {
  ...monospaceStyle,
  px: 0.5,
  borderRadius: "4px",
  backgroundColor: labelBacking,
  color: "text.secondary",
  fontSize: "10.5px",
  lineHeight: 1.3,
} as const;

// A capability: its pill with the level's letter (a mission its small
// accent circle), its name, and beneath them its lift and the centre's hosts
// that claim it; one with no provider line says so, muted
export const CapabilityNode: React.FC<NodeProps<CapabilityFlowNode>> = ({ data }) => {
  const { capability, noProvider, selected, dimmed, onSelect } = data;
  const name = capabilityName(capability);
  const mission = capability.level === "operator";
  return (
    <Box
      {...selectable(onSelect)}
      aria-label={name}
      aria-pressed={selected}
      data-testid="capability-node"
      data-level={capability.level}
      data-dimmed={dimmed ? "true" : "false"}
      sx={{ ...NODE_SX, opacity: dimmed ? NODE_DIMMED : 1 }}
    >
      <NodeHandles top={markHeight(capability.level) / 2} />
      {mission ? (
        <MissionDot selected={selected} />
      ) : (
        <CapabilityPill level={capability.level} selected={selected} />
      )}
      <NameLine name={name} />
      <Box
        component="span"
        sx={COUNT_SX}
        title={`lift ${liftText(capability.lift)} · ${capability.centre_hosts} of the centre's hosts claim it`}
      >
        <Box component="span" sx={{ color: (theme) => theme.palette.chart.accentText }}>
          {liftText(capability.lift)}
        </Box>
        {` · ${capability.centre_hosts}`}
      </Box>
      {noProvider && (
        <Box
          component="span"
          data-testid="no-provider"
          sx={{ ...COUNT_SX, fontFamily: "inherit", color: "text.disabled" }}
        >
          {noProvider}
        </Box>
      )}
    </Box>
  );
};

// A category: its box (solid on record inside, dashed could provide,
// hatched under the floor), its name and its counts
export const CategoryNode: React.FC<NodeProps<CategoryFlowNode>> = ({ data }) => {
  const { category, selected, dimmed, onSelect } = data;
  const name = categoryName(category);
  return (
    <Box
      {...selectable(onSelect)}
      aria-label={name}
      aria-pressed={selected}
      data-testid="category-node"
      data-provider={category.inside ? "inside" : "could"}
      data-thin={category.thin ? "true" : "false"}
      data-dimmed={dimmed ? "true" : "false"}
      sx={{ ...NODE_SX, opacity: dimmed ? NODE_DIMMED : 1 }}
    >
      <NodeHandles top={BOX_SIZE.height / 2} />
      <CategoryBox
        label={category.other ? "?" : shortName(name)}
        inside={category.inside}
        thin={category.thin}
        endItem={ringOfCategory(category) === 4}
        selected={selected}
      />
      <NameLine name={name} quiet={!category.inside || category.thin} />
      <Box component="span" sx={COUNT_SX}>
        {categoryCaption(category)}
      </Box>
    </Box>
  );
};

export interface MissionEdgeData extends Record<string, unknown> {
  kind: "spoke" | "provider";
  // The provider edge; null for a spoke
  edge: MissionEdge | null;
  capability: string;
  category: string | null;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
  // What the line says, where the pointer rests on it
  reading: string;
  // One of its ends is selected: drawn in the accent
  active: boolean;
  dimmed: boolean;
}

export type MissionFlowEdge = Edge<MissionEdgeData, "mission">;

// The stroke of a line by what it says: a spoke grey, an inside provider
// darker and solid, teal when a part is stated to enable the capability, a
// could provider dashed
export const lineStroke = (
  data: Pick<MissionEdgeData, "kind" | "edge">,
  chart: ChartPalette,
): { stroke: string; dash?: string; teal: boolean } => {
  const lines = lineColoursOf(chart);
  if (data.kind === "spoke" || !data.edge) {
    return { stroke: lines.spoke, teal: false };
  }
  if (data.edge.provider === "could") {
    return { stroke: lines.could, dash: COULD_DASH, teal: false };
  }
  return data.edge.enables > 0
    ? { stroke: lines.enables, teal: true }
    : { stroke: lines.inside, teal: false };
};

// Width (px) of the band along a line that answers the pointer
const HOVER_WIDTH = 12;

// A line of the rings. The lines of the selection take the accent; the
// rest stand back while something is selected.
export const MissionEdgeLine: React.FC<EdgeProps<MissionFlowEdge>> = ({ id, data }) => {
  const theme = useTheme();
  if (!data) {
    return null;
  }
  const path = `M ${data.x1} ${data.y1} L ${data.x2} ${data.y2}`;
  const { stroke, dash, teal } = lineStroke(data, theme.palette.chart);
  const drawn = data.active ? theme.palette.accent.main : stroke;
  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        interactionWidth={0}
        style={{
          stroke: drawn,
          strokeWidth: data.width,
          strokeDasharray: dash,
          strokeLinecap: "round",
          opacity: data.dimmed ? LINE_DIMMED : teal && !data.active ? ENABLES_OPACITY : 1,
          transition: "opacity 120ms",
        }}
      />
      <Tooltip title={data.reading} followCursor enterDelay={150}>
        <path
          d={path}
          fill="none"
          stroke="transparent"
          strokeWidth={HOVER_WIDTH}
          data-testid="mission-edge"
          data-kind={data.kind}
          data-provider={data.edge?.provider ?? ""}
          data-enables={data.edge?.enables ?? 0}
          data-capability={data.capability}
          data-category={data.category ?? ""}
          data-stroke={drawn}
          data-dash={dash ?? ""}
          data-width={data.width}
          data-active={data.active ? "true" : "false"}
        />
      </Tooltip>
    </>
  );
};

// What a line says in a sentence, with the names of its ends
export const edgeReading = (
  kind: "spoke" | "provider",
  edge: MissionEdge | null,
  capability: MissionCapability | undefined,
  category: MissionCategory | undefined,
  centre: string,
): string => {
  const what = capability ? capabilityName(capability) : (edge?.capability ?? "");
  if (kind === "spoke" || !edge) {
    return capability
      ? `${capability.centre_hosts} of the hosts of ${centre} claim ${what} · lift ${liftText(capability.lift)}`
      : what;
  }
  const who = category ? categoryName(category) : edge.category;
  if (edge.provider === "could") {
    return `${who} could provide ${what}: ${counted(edge.products, "product")} claim it, none on record inside these hosts`;
  }
  const enables = edge.enables > 0 ? ` · ${edge.enables} stated "enables"` : "";
  return `${who} inside ${counted(edge.hosts, "host")} said to have or enable ${what} · ${counted(edge.products, "product")} · ${counted(edge.makers, "maker")}${enables}`;
};
